import {
  Typography,
  CircularProgress,
  MenuItem,
  Box,
  Card,
  CardContent,
  Stack,
  SelectChangeEvent,
} from "@mui/material";
import { LeadBox, CustomSelect, LeadGrid } from "../../components";
import { useHome } from "./hook";
import { User } from "../../model/User";
import Grid from "@mui/material/Unstable_Grid2";
import InboxIcon from "@mui/icons-material/Inbox";
import { HomeMessageCard } from "../../components/HomeMessageCard";

// ✅ Moved outside Home to avoid remounting on every render
const DealerSelect = ({
  user,
  onChange,
}: {
  user: User;
  onChange: (event: SelectChangeEvent<unknown>) => void;
}) => (
  <Box mt={4}>
    <CustomSelect
      sx={{
        width: "100%",
        height: 35,
        mb: 2,
        maxWidth: 300,
      }}
      label="Accounts"
      value={user.UserAccount ?? "Select an account"}
      onChange={onChange} // ✅ wired up
    >
      <MenuItem value={"Select an account"}>Select an account</MenuItem>
      {user.Accounts.map((account) => (
        <MenuItem key={account.dealerId} value={account.dealerId}>
          {account.companyName}
        </MenuItem>
      ))}
    </CustomSelect>
  </Box>
);

// ✅ Moved outside Home to avoid remounting on every render
const HomeMessageList = ({ user }: { user: User }) => (
  <Stack mt={2} spacing={2}>
    {user.HomeMessages.map((message, index) => (
      <HomeMessageCard
        key={index}
        type={message.type}
        content={message.message}
      />
    ))}
  </Stack>
);

// ✅ Moved outside Home to avoid remounting on every render
const EmptyState = ({ message }: { message: string }) => (
  <Box
    sx={{
      minHeight: "60vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      px: 2,
    }}
  >
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1.5,
        px: 4,
        py: 3,
        backgroundColor: "#F7FAFD",
        borderRadius: 3,
        border: "1px solid #E3ECF5",
        boxShadow: "0px 4px 12px rgba(0,0,0,0.06)",
        maxWidth: 420,
        textAlign: "center",
      }}
    >
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: "50%",
          backgroundColor: "#E8F1FB",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 0.5,
        }}
      >
        <InboxIcon sx={{ fontSize: 28, color: "#5B8FD8" }} />
      </Box>

      <Typography
        variant="body2"
        sx={{
          fontWeight: 500,
          color: "#3A4A5E",
          lineHeight: 1.5,
        }}
      >
        {message}
      </Typography>
    </Box>
  </Box>
);

export const Home = () => {
  const {
    metrics,
    dashboards,
    selectedDashboard,
    dashboardValue,
    isMetricsLoading,
    isLeadsLoading,
    hideMetrics,
    hideTables,
    columnAccess,
    moveToDashboard,
    onChange, // ✅ destructured from hook
    page,
    pageSize,
    sortModel,
    onPageChange,
    onPageSizeChange,
    onChangeOrder,
  } = useHome();

  const user = new User();

  const hasMetrics = metrics.length > 0;
  const hasDashboards = dashboards.length > 0;
  const hasHomeContent = hasMetrics || hasDashboards;
  const isDoneLoading = !isMetricsLoading && !isLeadsLoading;
  const showGlobalEmptyState = isDoneLoading && !hasHomeContent;

  return (
    <>
      {/* HEADER */}
      <Grid container sx={{ mb: 2 }}>
        <Grid xs={12}>
          <Typography
            variant="h4"
            sx={{ color: "#1BA0E9", fontWeight: 600, fontSize: 25 }}
          >
            Hello {user.DisplayUserName}!
          </Typography>

          {!!user.HomeMessages.length && <HomeMessageList user={user} />}
        </Grid>
      </Grid>

      {/* GLOBAL EMPTY STATE */}
      {showGlobalEmptyState && (
        <EmptyState message="Your home page has no items configured. Please set your preferences in the 'Edit Profile' page or contact your administrator." />
      )}

      {/* ACCOUNT SELECT (ONLY IF THERE IS CONTENT) */}
      {!showGlobalEmptyState && user.Roles[0].secLevel !== 1 && hasHomeContent && (
        <Grid xs={12} md={4}>
          <DealerSelect user={user} onChange={onChange} /> {/* ✅ passing both props */}
        </Grid>
      )}

      {/* METRICS LOADING */}
      {isMetricsLoading && !hideMetrics && !showGlobalEmptyState && (
        <Box
          sx={{
            width: "100%",
            height: 165,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {/* METRICS */}
      {!isMetricsLoading && !hideMetrics && hasMetrics && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {metrics.map((metric, index) => (
            <Grid xs={12} sm={6} md={3} key={metric.id}>
              <LeadBox
                index={index}
                label={metric.description}
                icon={metric.icon}
                data={metric.value}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* DASHBOARDS */}
      {!hideTables && hasDashboards && (
        <Grid container>
          <Grid xs={12}>
            <Card sx={{ borderRadius: 4 }}>
              <CardContent>
                <Grid container>
                  {dashboards.map((val) => (
                    <Grid
                      key={val.id}
                      md={3}
                      lg={2}
                      xl={2}
                      onClick={() => moveToDashboard(val.id)}
                    >
                      <Typography
                        sx={{
                          color: "#1BA0E9",
                          fontSize: 14,
                          fontWeight: 600,
                          py: 1,
                          borderRadius: 2,
                          backgroundColor:
                            dashboardValue === val.id
                              ? "rgba(0,0,0,0.07)"
                              : "inherit",
                          textAlign: "center",
                          cursor: "pointer",
                        }}
                      >
                        {val.description}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>

                {selectedDashboard &&
                  selectedDashboard.items &&
                  selectedDashboard.items.length > 0 ? (
                  <LeadGrid
                    partialColumns={
                      selectedDashboard.items.length
                        ? Object.keys(selectedDashboard.items[0])
                        : []
                    }
                    renderRefresh
                    loading={isLeadsLoading}
                    callLeads={selectedDashboard.items}
                    page={page}
                    pageSize={pageSize}
                    total={selectedDashboard.quantity}
                    onPageChange={onPageChange}
                    onPageSizeChange={onPageSizeChange}
                    onSortModelChange={onChangeOrder}
                    sortModel={sortModel}
                    columnAccess={columnAccess}
                  />
                ) : (
                  <EmptyState message="No data available for this dashboard." />
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </>
  );
};